import React, { useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { useUserAccessMatrix } from "newui/features/RolesPermissions/hooks/useUserAccessMatrix";
import { ScopeLabel } from "newui/features/RolesPermissions/components/ScopeLabel";
import { scopeTypeStyle } from "newui/features/RolesPermissions/utils/scopeTypeStyles";
import type {
	AccessMatrixCell,
	AccessMatrixScopedAssignment,
	UserAccessMatrix,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface UserAccessMatrixModalProps {
	isOpen: boolean;
	userId?: number;
	userName?: string;
	onClose: () => void;
}

interface CellDisplay {
	label: string;
	className: string;
}

/**
 * PRO-731 (2.5) — the five states a role/module intersection can be in.
 *
 * The colours carry the meaning at a glance, so the two "you have nothing here"
 * states share grey deliberately: N/A and No Access differ in WHY, not in what
 * the user can do, and the label is what distinguishes them.
 */
const CELL_DISPLAY: Record<AccessMatrixCell, CellDisplay> = {
	n_a: { label: "N/A", className: "bg-gray-50 border-gray-200 text-gray-400" },
	none: { label: "No Access", className: "bg-gray-50 border-gray-200 text-gray-500" },
	view: { label: "View", className: "bg-blue-50 border-blue-300 text-blue-800" },
	specific: { label: "Specific", className: "bg-amber-50 border-amber-300 text-amber-900" },
	full: { label: "Full", className: "bg-emerald-50 border-emerald-300 text-emerald-800" },
};

type AccessTab = "roles" | "scoped";

// PRO-730 — negative ids are tour-fixture dummy users (see
// guides/features/Auth/tourFixtures.ts). The "View a user's access" guided tour
// opens this modal for one so it can point at the tabs without a real user, so
// the query below must never fire for it — static demo content renders instead.
const isDummyAccessUserId = (id: number | undefined): boolean =>
	typeof id === "number" && id < 0;

const TOUR_DEMO_MATRIX: UserAccessMatrix = {
	user_id: -1,
	company_side: "procurer",
	roles: [{ id: -1, name: "Team Member", side: "both" }],
	modules: [
		{ key: "rfx_module", name: "RFx Module (Procurer lifecycle)", applies_to: "procurer" },
		{ key: "portfolio_projects_assets", name: "Portfolio / Projects / Assets", applies_to: "procurer" },
	],
	cells: {
		"-1": { rfx_module: "view", portfolio_projects_assets: "specific" },
	},
	scoped_assignments: [
		{
			assignment_id: -1,
			role_id: -1,
			role_name: "Team Member",
			scope_type: "project",
			scope_id: -1,
			scope_label: "Demo Project",
		},
	],
};

const AccessCell = ({ state }: { state: AccessMatrixCell }) => {
	const display = CELL_DISPLAY[state] ?? CELL_DISPLAY.none;

	return (
		<span
			className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${display.className}`}
			data-testid={`access-cell-${state}`}
		>
			{display.label}
		</span>
	);
};

const ScopedAssignmentRow = ({ assignment }: { assignment: AccessMatrixScopedAssignment }) => {
	// Portfolio green, Project yellow, RFx blue — the shared palette, so a
	// reader scanning this list can tell the three levels apart without reading
	// each badge. See utils/scopeTypeStyles.
	const style = scopeTypeStyle(assignment.scope_type);

	return (
		<li
			className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5"
			data-testid={`user-access-scoped-row-${assignment.assignment_id}`}
		>
			<div className="flex flex-col">
				<span className="text-sm font-medium text-gray-800">{assignment.role_name}</span>
				{/*
				  * Full name, not truncated: this list is a full-width row, so there
				  * is no layout to protect and nothing is gained by hiding characters
				  * behind a tooltip.
				  */}
				<ScopeLabel
					label={assignment.scope_label}
					truncate={false}
					className="text-xs text-gray-500"
				/>
			</div>
			<span
				className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.chip}`}
				data-testid={`user-access-scope-type-${assignment.scope_type}`}
			>
				{style.label}
			</span>
		</li>
	);
};

/**
 * PRO-413 #UM1 / PRO-731 — read-only view of a single user's access.
 *
 * Two entry points, ONE payload: My Profile and User Management both render
 * from `GET /rbac/users/{id}/access-matrix`, whose gate admits the subject as
 * well as a System Administrator. They previously ran on different queries —
 * the admin path reading System-Admin-only endpoints, the self path deriving
 * rows from the effective-permissions payload — and so showed different rows,
 * different labels and a different set of tabs for the same user. There is
 * deliberately no `mode` prop any more: with one source there is nothing left
 * for a caller to vary.
 *
 *  - "All Roles": a column per assigned role, a row per module, and the level
 *    that role grants on that module.
 *  - "Project & RFx access": the scope-limited assignments behind any
 *    "Specific" cell, named rather than numbered.
 */
export const UserAccessMatrixModal = ({
	isOpen,
	userId,
	userName,
	onClose,
}: UserAccessMatrixModalProps) => {
	const isDummy = isDummyAccessUserId(userId);
	const query = useUserAccessMatrix(userId, isOpen && !isDummy);
	const [tab, setTab] = useState<AccessTab>("roles");

	const matrix = isDummy ? TOUR_DEMO_MATRIX : query.data;
	const isLoading = !isDummy && query.isLoading;
	const isError = !isDummy && query.isError;

	const roles = matrix?.roles ?? [];
	const modules = matrix?.modules ?? [];
	const cells = matrix?.cells ?? {};
	const scopedAssignments = matrix?.scoped_assignments ?? [];

	const title = userName ? `Access for ${userName}` : "User access";

	const tabButtonClass = (active: boolean): string =>
		`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
			active
				? "border-blue-600 text-blue-700"
				: "border-transparent text-gray-500 hover:text-gray-700"
		}`;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			description="This user's resolved access — every assigned role, plus any project or RFx-specific grants."
			size="xxl"
			// The matrix owns the scrolling so its sticky header/first column stay
			// pinned — `position: sticky` resolves against the nearest scrolling
			// ancestor, so the body must NOT scroll too (PRO-718).
			fillBody
			primaryButton={{ text: "Close", btntype: "secondary", onClick: onClose }}
		>
			<div data-testid="user-access-matrix-modal" className="flex min-h-0 flex-1 flex-col gap-4">
				<div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={tab === "roles"}
						className={tabButtonClass(tab === "roles")}
						onClick={() => setTab("roles")}
						data-testid="user-access-tab-company"
					>
						All Roles
					</button>
					{scopedAssignments.length > 0 && (
						<button
							type="button"
							role="tab"
							aria-selected={tab === "scoped"}
							className={tabButtonClass(tab === "scoped")}
							onClick={() => setTab("scoped")}
							data-testid="user-access-tab-scoped"
						>
							Project &amp; RFx access
							<span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
								{scopedAssignments.length}
							</span>
						</button>
					)}
				</div>

				{isLoading && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
						<Spinner size="sm" /> Loading access…
					</div>
				)}

				{!isLoading && isError && (
					<div className="py-10 text-center text-sm text-red-600" data-testid="user-access-error">
						Could not load this user&apos;s access. Please try again.
					</div>
				)}

				{!isLoading && !isError && tab === "roles" && (
					<div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
						{roles.length === 0 ? (
							<p className="py-10 text-center text-sm text-gray-500" data-testid="user-access-no-roles">
								This user has no roles assigned.
							</p>
						) : modules.length === 0 ? (
							// The server filters rows to the company's own side, so an
							// empty list means it could not work out which side that is
							// (no company, or a soft-deleted one) — not that the company
							// has no modules.
							<p className="py-10 text-center text-sm text-gray-500" data-testid="user-access-no-modules">
								No modules apply to this company.
							</p>
						) : (
							<table className="min-w-full border-collapse text-sm">
								<thead className="sticky top-0 z-10">
									<tr
										className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500"
										data-testid="user-access-role-columns"
									>
										<th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-semibold">Module</th>
										{roles.map((role) => (
											<th
												key={role.id}
												className="bg-gray-50 px-3 py-2 text-center font-semibold"
												data-testid={`user-access-role-column-${role.id}`}
											>
												{role.name}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{modules.map((module) => (
										<tr
											key={module.key}
											className="border-t border-gray-100"
											data-testid={`user-access-row-${module.key}`}
										>
											<th
												scope="row"
												className="sticky left-0 z-[1] bg-white px-3 py-2 text-left font-medium text-gray-700"
											>
												{module.name}
											</th>
											{roles.map((role) => (
												<td key={role.id} className="px-3 py-2 text-center">
													<AccessCell state={cells[String(role.id)]?.[module.key] ?? "none"} />
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				)}

				{!isLoading && !isError && tab === "scoped" && (
					<div className="min-h-0 flex-1 overflow-auto" data-testid="user-access-scoped-list">
						{scopedAssignments.length === 0 ? (
							<p className="py-10 text-center text-sm text-gray-500" data-testid="user-access-scoped-empty">
								No project or RFx-specific access.
							</p>
						) : (
							<ul className="flex flex-col gap-2">
								{scopedAssignments.map((assignment) => (
									<ScopedAssignmentRow key={assignment.assignment_id} assignment={assignment} />
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
};
